import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  CommunicationIntegrationKind,
  KIND_TO_LINKED_CHANNEL,
} from "@/types/communication-integration";
import { useSetIntegrationEnabled } from "../hooks/communication-integrations.hooks";

interface DisableIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId: string;
  kind: CommunicationIntegrationKind;
}

export const DisableIntegrationDialog: React.FC<
  DisableIntegrationDialogProps
> = ({ open, onOpenChange, hotelId, kind }) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useSetIntegrationEnabled(hotelId);

  const kindLabel = t(`communicationChannels.kinds.${kind}.label`);
  const channelLabel = t(
    `communicationChannels.channels.${KIND_TO_LINKED_CHANNEL[kind]}.label`,
  );

  const handleConfirm = () => {
    mutate(
      { kind, enabled: false },
      {
        onSuccess: () => {
          toast.success(t("communicationChannels.disable.success"), {
            description: t("communicationChannels.disable.cascadeDescription", {
              channel: channelLabel,
            }),
          });
          onOpenChange(false);
        },
        onError: () => {
          toast.error(t("communicationChannels.disable.error"));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("communicationChannels.disable.title", { kind: kindLabel })}
          </DialogTitle>
          <DialogDescription>
            {t("communicationChannels.disable.description")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="new_outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t("communicationChannels.actions.cancel")}
          </Button>
          <Button
            type="button"
            variant="new_destructive"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {t("communicationChannels.disable.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
